import axios, { AxiosError, AxiosInstance, CancelTokenSource } from 'axios'
import { generateRecaptchaToken } from '../common/recapctcha'
import {
  dropdownOptions,
  PlacenameDataType,
  PlacenameDocument,
  PlacenameSearchOptionData,
  SearchOptionsData,
} from '../components/placenames-components/slice'
import {
  PLACENAME_DOCUMENT_BASE,
  PLACENAME_SEARCH_FILTER,
  PLACENAME_SEARCH_HOST,
  PLACENAME_SEARCH_PATH,
  PLACENAMES_MEDIA_HOST,
  RECAPTCHA_ENABLED,
} from '../environment'
import RecaptchaRequiredError from '../errors/RecaptchaRequiredError'
import { ApiResponse } from './type'

let source: CancelTokenSource

export const cancelPlacenameRequest = () => {
  if (source) {
    source.cancel('CANCEL')
  }
}

const AXIOS_CONFIG = {
  withCredentials: true,
}

class PlaceServiceAPI {
  client: AxiosInstance

  constructor() {
    this.client = axios.create(AXIOS_CONFIG)
  }


  fetchCandidatePlaces = async (payload: SearchOptionsData) => {
    // require recaptcha
    if (RECAPTCHA_ENABLED) {
      const isV3Captcha = !payload.recaptchaV2Code
      const recaptchaToken = await generateRecaptchaToken()
      if (typeof payload.searchFilters === 'string') {
        return await this.fetchCandidatePlacesWithRecaptcha(recaptchaToken, payload, isV3Captcha)
      } else {
        return await this.fetchFilteredCandidatePlacesWithRecaptcha(recaptchaToken, payload, isV3Captcha)
      }
    } else {
      if (typeof payload.searchFilters === 'string') {
        return await this.fetchCandidatePlacesWithoutRecaptcha(payload)
      } else {
        return await this.fetchFilteredCandidatePlacesWithoutRecaptcha(payload)
      }

    }
  }

  /**
   * Fetch candidate addresses asynchronously. update valuationData in the redux store with the results.
   *
   * @returns {Promise<void>}
   */

  fetchCandidatePlacesWithoutRecaptcha = async (payload: SearchOptionsData) => {
    try {
      const CancelToken = axios.CancelToken
      source = CancelToken.source()
      const response = await this.client.get<ApiResponse<PlacenameDataType[]>>(PLACENAME_SEARCH_PATH + '/json', {
        params: {
          searchterm: payload.searchFilters,
          phonetics: payload.phoneticSearch,
        },
        cancelToken: source.token,
      })

      return response.data.Output
    } catch (error: any) {
      return this.handleError(error)
    }
  }

  /**
   * Fetch Valuation Data asynchronously. update valuationData in the redux store with the results.
   *
   * @returns {Promise<void>}
   */

  fetchCandidatePlacesWithRecaptcha = async (captchaCode: string, payload: SearchOptionsData, isV3recaptcha: boolean) => {
    try {
      const CancelToken = axios.CancelToken
      source = CancelToken.source()
      const response = await this.client.get<ApiResponse<PlacenameDataType[]>>(PLACENAME_SEARCH_PATH + '/jsonWithRecaptcha', {
        params: {
          searchterm: payload.searchFilters,
          [isV3recaptcha ? 'recaptchaV3Key' : 'recaptchaV2Key']: captchaCode,
          phonetics: payload.phoneticSearch,
        },
        cancelToken: source.token,
      })

      return response.data.Output
    } catch (error: any) {
      return this.handleError(error)
    }
  }

  fetchFilteredCandidatePlacesWithoutRecaptcha = async (payload: SearchOptionsData) => {
    try {
      const CancelToken = axios.CancelToken
      source = CancelToken.source()
      const response = await this.client.post<ApiResponse<PlacenameDataType[]>>(PLACENAME_SEARCH_PATH + '/advanced/search', payload.searchFilters, { cancelToken: source.token })
      return response.data.Output
    } catch (error: any) {
      return this.handleError(error)
    }
  }

  fetchFilteredCandidatePlacesWithRecaptcha = async (captchaCode: string, payload: SearchOptionsData, isV3recaptcha: boolean) => {
    try {
      const CancelToken = axios.CancelToken
      source = CancelToken.source()
      const response = await this.client.post<ApiResponse<PlacenameDataType[]>>(PLACENAME_SEARCH_PATH + '/advanced/search', payload.searchFilters, {
        headers: {
          'X-XSRF-TOKEN': this.getCsrfToken(),
        },
        params: {
          [isV3recaptcha ? 'recaptchaV3Key' : 'recaptchaV2Key']: captchaCode,
        },
        cancelToken: source.token,
      })
      return response.data.Output
    } catch (error: any) {
      return this.handleError(error)
    }
  }

  fetchPlacenameSearchOptions = async (type: string) => {
    try {
      let recaptchaToken: string | undefined
      if (RECAPTCHA_ENABLED) {
        recaptchaToken = await generateRecaptchaToken()
      }
      const response = await this.client.get<PlacenameSearchOptionData[]>(PLACENAME_SEARCH_FILTER + '/json', {
        params: {
          type: type,
          recaptchaV3Key: recaptchaToken,
        },
      })
      let count = 0
      let results: dropdownOptions[] = response.data
        .sort((a, b) => a.value > b.value ? 1 : -1)
        .map((option: { value: string }) => ({
          id: count++,
          text: option.value,
          value: option.value,
        }))
      return results
    } catch (error: any) {
      return this.handleError(error)
    }
  }

  fetchCSRFToken = async () => {
    const response = await this.client.get<{ token: string }>(PLACENAME_SEARCH_HOST + '/csrf')
    return response.data.token
  }

  fetchReport = async (searchFilters: SearchOptionsData) => {
    let recaptchaToken: string | undefined
    if (RECAPTCHA_ENABLED) {
      recaptchaToken = await generateRecaptchaToken()
    }
    const token = await this.fetchCSRFToken()
    try {
      const response = await this.client.post<BlobPart>(PLACENAME_SEARCH_HOST + '/reports/extract', searchFilters, {
        responseType: 'blob',
        headers: {
          'X-CSRF-TOKEN': token,
        },
        params: {
          recaptchaV3Key: recaptchaToken,
        },
      })
      return response.data
    } catch (error: any) {
      return this.handleError(error)
    }
  }

  fetchPlacename = async (pointId: string) => {
    try {
      let recaptchaToken: string | undefined
      if (RECAPTCHA_ENABLED) {
        recaptchaToken = await generateRecaptchaToken()
      }
      const response = await this.client.get<PlacenameDataType>(PLACENAME_SEARCH_HOST + '/data', {
        params: {
          pointId: pointId,
          recaptchaV3Key: recaptchaToken,
        },
      })
      return response.data
    } catch (error: any) {
      return this.handleError(error)
    }
  }

  fetchPlacenameDocumentsFor = async (placeId: string) => {
    try {
      let recaptchaToken: string | undefined
      if (RECAPTCHA_ENABLED) {
        recaptchaToken = await generateRecaptchaToken()
      }
      const response = await this.client.get<PlacenameDocument[]>(PLACENAME_DOCUMENT_BASE + placeId, {
        params: {
          recaptchaV3Key: recaptchaToken,
        },
      })
      return response.data
    } catch (error: any) {
      return this.handleError(error)
    }
  }

  fetchPlacenameDocumentFor = async (placeId: number, mediaId: number) => {
    try {
      let recaptchaToken: string | undefined
      if (RECAPTCHA_ENABLED) {
        recaptchaToken = await generateRecaptchaToken()
      }
      const response = await this.client.get<BlobPart>(PLACENAME_DOCUMENT_BASE + placeId + '/media/' + mediaId, {
        responseType: 'blob',
        params: {
          recaptchaV3Key: recaptchaToken,
        },
      })
      return response.data
    } catch (error: any) {
      return this.handleError(error)
    }
  }

  fetchPlacenameMedia = async (placeId: string) => {
    try {
      const response = await this.client.get(PLACENAMES_MEDIA_HOST + placeId)
      return response.data
    } catch (error: any) {
      return this.handleError(error)
    }
  }

  getCsrfToken = () => {
    return document.cookie.replace(/(?:(?:^|.*;\s*)XSRF-TOKEN\s*=\s*([^;]*).*$)|^.*$/, '$1')
  }

  handleError = (error: any) => {
    if (error && error.response && error.response.data && error.response.data.type === 'INVALID_CAPTCHA') {
      // V3 recaptcha failed (low score) force V2 recaptcha
      throw new RecaptchaRequiredError('error.response.data.type', 'Recaptcha failed (V3), manual Recaptcha required! (V2)')
    } else if (error.message !== 'CANCEL') {
      throw error
    }
  }
}

const placeServiceAPI = new PlaceServiceAPI()

export default placeServiceAPI
